import { Component } from "react"
import PropTypes from "prop-types"
import find from "lodash/find"
import indexOf from "lodash/indexOf"
import EndpointTestAssertions from "partners/components/endpoint_test/assertions"
import EndpointTestCaseMessage from "partners/components/endpoint_test/test_case_message"
import { post } from "shared/lib/api"

export default class AuthenticationTestRequestToken extends Component {
  constructor(props) {
    super(props)
    this.state = {
      enabled: false,
      isShowing: false,
      status: "initial",
      errorMessage: "Could not initiate request, please try again.",
    }
    this.getStatusClass = this.getStatusClass.bind(this)
    this.requestToken = this.requestToken.bind(this)
    this.toggle = this.toggle.bind(this)
  }

  componentDidMount() {
    if (this.props.code && this.props.code.length) {
      this.requestToken()
    }
  }

  getStatusClass() {
    if (this.state.status === "completed") {
      return find(
        this.state.assertions,
        assertion => assertion.status === "failing"
      )
        ? "failing"
        : "passing"
    } else if (this.state.status === "error") {
      return "failing"
    }
    return ""
  }

  toggle() {
    this.setState({ isShowing: !this.state.isShowing })
  }

  requestToken() {
    this.setState({ status: "working", enabled: true })
    post(this.props.path, { code: this.props.code, code_verifier: this.props.codeVerifier })
      .then(response => {
        const statusCode = response.data.response.statusCode
        let body = response.data.response.body
        try {
          body = JSON.parse(body)
        } catch (e) {} // eslint-disable-line no-empty
        const assertions = {
          status_code_200: {
            label: "Status Code is 200",
            status:
              indexOf([200, 201], statusCode) === -1 ? "failing" : "passing",
          },
          body_valid_json: {
            label: "Body is valid JSON",
            status: body ? "passing" : "failing",
          },
          response_is_object: {
            label: "Response is an object",
            status: body && typeof body === "object" ? "passing" : "failing",
          },
          token_provided: {
            label: "Access token is provided",
            status: body && body.access_token ? "passing" : "failing",
          },
          token_is_string: {
            label: "Access token is a string",
            status:
              body && typeof body.access_token === "string"
                ? "passing"
                : "failing",
          },
          token_length: {
            label: "Access token is at least one character long",
            status:
              body && body.access_token && body.access_token.length > 0
                ? "passing"
                : "failing",
          },
        }
        if (["oauth_refresh_token", "oauth_pkce_refresh_token"].includes(this.props.service.authentication_type)) {
          assertions.refresh_token_provided = {
            label: "Refresh token is provided",
            status: body && body.refresh_token ? "passing" : "failing",
          }
          assertions.refresh_token_is_string = {
            label: "Refresh token is a string",
            status:
              body &&
              body.refresh_token &&
              typeof body.refresh_token === "string"
                ? "passing"
                : "failing",
          }
          assertions.refresh_token_length = {
            label: "Refresh token is at least one character long",
            status:
              body &&
              body.refresh_token &&
              typeof body.refresh_token === "string" &&
              body.refresh_token.length > 0
                ? "passing"
                : "failing",
          }
          if (body && body.expires_in) {
            assertions.expires_in_is_a_positive_number = {
              label: "Expires in is a positive number",
              status:
                typeof body.expires_in === "number" && body.expires_in > 0
                  ? "passing"
                  : "failing",
            }
          }
        } else {
          assertions.token_non_expiring = {
            label: "Access token is non-expiring",
            status:
              body && body.access_token && !body.expires_in && !body.expires_at
                ? "passing"
                : "failing",
          }
        }
        window.dispatchEvent(
          new CustomEvent("tokensReceived", {
            detail: {
              accessToken: body.access_token,
              refreshToken: body.refresh_token,
            },
          })
        )
        this.setState(
          {
            assertions,
            request: response.data.request,
            response: response.data.response,
            status: "completed",
          },
          () => {
            window.dispatchEvent(
              new CustomEvent("statusReceived", {
                detail: {
                  componentKey: "requestToken",
                  componentStatus: this.getStatusClass(),
                },
              })
            )
          }
        )
      })
      .catch(response => {
        let { errorMessage } = this.state
        if (response.responseText) {
          try {
            errorMessage = JSON.parse(response.responseText).data.error.code
          } catch (e) {} // eslint-disable-line no-empty
        }
        this.setState({ status: "error", errorMessage })
        window.dispatchEvent(
          new CustomEvent("statusReceived", {
            detail: {
              componentKey: "requestToken",
              componentStatus: "failing",
            },
          })
        )
      })
  }

  render() {
    const statusClass = this.getStatusClass()
    return (
      <div
        className={`endpoint publish-item ${statusClass} ${
          this.state.isShowing ? "expanded" : "collapsed"
        }`}
      >
        <header onClick={this.toggle}>Request access token</header>
        {this.state.isShowing ? (
          <div>
            {this.state.status === "completed" ? (
              <EndpointTestCaseMessage
                type="request"
                message={this.state.request}
              />
            ) : null}
            {this.state.status === "completed" ? (
              <EndpointTestCaseMessage
                type="response"
                message={this.state.response}
              />
            ) : null}
            {this.state.status === "completed" ? (
              <EndpointTestAssertions assertions={this.state.assertions} />
            ) : null}
            {this.state.status === "error" ? (
              <div>{this.state.errorMessage}</div>
            ) : null}
            {this.state.status === "working" ? <div>Working…</div> : null}
          </div>
        ) : null}
      </div>
    )
  }
}

AuthenticationTestRequestToken.propTypes = {
  service: PropTypes.object.isRequired,
  code: PropTypes.string,
  codeVerifier: PropTypes.string,
  path: PropTypes.string.isRequired,
}
